import React, { useState } from 'react';
import { ChevronDownIcon, Wand2Icon, SparklesIcon } from 'lucide-react';
import { StoryReviewModal } from '../components/StoryReviewModal';
type AgeGroup = 'preschool' | 'kindergarten' | 'grade-school';
interface StoryBasics {
  characters: string;
  setting: string;
  plot: string;
  beginning: string;
  ending: string;
}
interface StoryDetails {
  audience: string;
  title: string;
  tone: string;
  writingStyle: string;
  illustrationStyle: string;
  specialTouches: string;
}
export const CreatorMode = () => {
  const [currentStep, setCurrentStep] = useState<'story' | 'illustrations'>('story');
  const [showReview, setShowReview] = useState(false);
  const [storyBasics, setStoryBasics] = useState<StoryBasics>({
    characters: '',
    setting: '',
    plot: '',
    beginning: '',
    ending: ''
  });
  const [storyDetails, setStoryDetails] = useState<StoryDetails>({
    audience: '',
    title: '',
    tone: '',
    writingStyle: '',
    illustrationStyle: '',
    specialTouches: ''
  });
  // Magic wand suggestions for each field
  const suggestions = {
    characters: ['A brave little dragon who loves to bake cookies but always burns them accidentally', 'Two best friends - a wise old turtle and a playful young rabbit who love solving mysteries', 'A group of musical penguins who dream of starting their own band'],
    setting: ['A magical treehouse that changes location every full moon, filled with talking books and floating teacups', 'An underwater city where sea creatures and merpeople live in houses made of coral and pearls', 'A colorful candy village with streets made of licorice and houses built from gingerbread'],
    plot: ['A young hero discovers they have a special power, but using it always leads to funny mix-ups. With help from new friends, they learn to master their gift just in time to save the day.', 'During the biggest celebration of the year, something goes hilariously wrong. Our characters must work together, using each of their unique talents to fix things before the party ends.', 'A mysterious map leads to an unexpected adventure. Along the way, our heroes make new friends, solve puzzles, and discover that the real treasure was the journey itself.'],
    audience: ['Preschool (2-4)', 'Kindergarten (5-6)', 'Grade School (6+)'],
    tone: ['Playful and silly with lots of giggles and funny moments', 'Gentle and warm like a cozy bedtime story', 'Exciting and adventurous with moments of wonder'],
    title: ['The Magical Day That Changed Everything', 'Adventures in Rainbow Valley', 'The Secret of Friendship Forest']
  };
  const handleSuggest = (field: keyof typeof suggestions) => {
    const randomSuggestion = () => {
      const options = suggestions[field];
      return options[Math.floor(Math.random() * options.length)];
    };
    if (field === 'audience') {
      // Cycle through age groups
      const currentIndex = suggestions.audience.indexOf(storyDetails.audience);
      const nextIndex = (currentIndex + 1) % suggestions.audience.length;
      setStoryDetails(prev => ({
        ...prev,
        audience: suggestions.audience[nextIndex]
      }));
      return;
    }
    if (field in storyBasics) {
      setStoryBasics(prev => ({
        ...prev,
        [field]: randomSuggestion()
      }));
    } else {
      setStoryDetails(prev => ({
        ...prev,
        [field]: randomSuggestion()
      }));
    }
  };
  // Generate title based on story content
  const generateTitleFromContent = () => {
    // In a real implementation, this would analyze the story content
    // For now, we'll use the suggestions
    handleSuggest('title');
  };
  return <div className="min-h-screen bg-[#FFFBF6] py-8 px-4" data-id="element-1252">
      <div className="max-w-3xl mx-auto" data-id="element-1253">
        <h1 className="text-3xl font-bold text-[#6B4B3E] mb-2" data-id="element-1254">Creator Mode</h1>
        <p className="text-[#6B4B3E]/80 mb-8" data-id="element-1255">
          Create your unique story with Story Bunny
        </p>
        {currentStep === 'story' ? <>
            {/* 1. Characters and Setting */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1256">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-1257">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-1258">
                  1
                </span>
                Characters and Setting
              </h2>
              {/* Characters */}
              <div className="space-y-6" data-id="element-1259">
                <div className="relative" data-id="element-1260">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1261">
                    Who is this story about?
                  </label>
                  <textarea value={storyBasics.characters} onChange={e => setStoryBasics(prev => ({
                ...prev,
                characters: e.target.value
              }))} placeholder="Describe your characters in detail. What makes them special? What do they look like?" className="w-full min-h-[120px] px-4 py-3 border-2 border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] text-[#6B4B3E]" data-id="element-1262" />
                  <button onClick={() => handleSuggest('characters')} className="absolute right-3 top-[38px] text-[#D4A088] hover:text-[#C28B73]" data-id="element-1263">
                    <Wand2Icon size={20} data-id="element-1264" />
                  </button>
                </div>
                {/* Setting */}
                <div className="relative" data-id="element-1265">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1266">
                    Where does this story take place?
                  </label>
                  <textarea value={storyBasics.setting} onChange={e => setStoryBasics(prev => ({
                ...prev,
                setting: e.target.value
              }))} placeholder="Describe every location in your story. What makes these places magical or special?" className="w-full min-h-[120px] px-4 py-3 border-2 border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] text-[#6B4B3E]" data-id="element-1267" />
                  <button onClick={() => handleSuggest('setting')} className="absolute right-3 top-[38px] text-[#D4A088] hover:text-[#C28B73]" data-id="element-1268">
                    <Wand2Icon size={20} data-id="element-1269" />
                  </button>
                </div>
              </div>
            </div>
            {/* 2. Story Structure */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1270">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-1271">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-1272">
                  2
                </span>
                What is this story about?
              </h2>
              <div className="space-y-6" data-id="element-1273">
                {/* Plot */}
                <div className="relative" data-id="element-1274">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1275">
                    What happens in this story?
                  </label>
                  <textarea value={storyBasics.plot} onChange={e => setStoryBasics(prev => ({
                ...prev,
                plot: e.target.value
              }))} placeholder="Describe the basic plot or story arc. If you don't know, that's okay - write any ideas even if they are fragments or just bullet points." className="w-full min-h-[120px] px-4 py-3 border-2 border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] text-[#6B4B3E]" data-id="element-1276" />
                  <button onClick={() => handleSuggest('plot')} className="absolute right-3 top-[38px] text-[#D4A088] hover:text-[#C28B73]" data-id="element-1277">
                    <Wand2Icon size={20} data-id="element-1278" />
                  </button>
                </div>
                {/* Beginning */}
                <div className="relative" data-id="element-1279">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1280">
                    How does this story start?
                  </label>
                  <textarea value={storyBasics.beginning} onChange={e => setStoryBasics(prev => ({
                ...prev,
                beginning: e.target.value
              }))} placeholder="Write what happens at the beginning. Where is the story set? What are the characters doing?" className="w-full min-h-[120px] px-4 py-3 border-2 border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] text-[#6B4B3E]" data-id="element-1281" />
                  <button onClick={() => handleSuggest('beginning')} className="absolute right-3 top-[38px] text-[#D4A088] hover:text-[#C28B73]" data-id="element-1282">
                    <Wand2Icon size={20} data-id="element-1283" />
                  </button>
                </div>
                {/* Ending */}
                <div className="relative" data-id="element-1284">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1285">
                    How does this story end?
                  </label>
                  <textarea value={storyBasics.ending} onChange={e => setStoryBasics(prev => ({
                ...prev,
                ending: e.target.value
              }))} placeholder="Write the ending. Where are they and what else can you write about what's happening?" className="w-full min-h-[120px] px-4 py-3 border-2 border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] text-[#6B4B3E]" data-id="element-1286" />
                  <button onClick={() => handleSuggest('ending')} className="absolute right-3 top-[38px] text-[#D4A088] hover:text-[#C28B73]" data-id="element-1287">
                    <Wand2Icon size={20} data-id="element-1288" />
                  </button>
                </div>
              </div>
            </div>
            {/* 3. Audience */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1289">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-1290">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-1291">
                  3
                </span>
                Audience
              </h2>
              <div className="relative" data-id="element-1292">
                <input type="text" value={storyDetails.audience} onChange={e => setStoryDetails(prev => ({
              ...prev,
              audience: e.target.value
            }))} placeholder="Describe your audience or use the magic wand to cycle through age groups" className="w-full px-4 py-2 border-2 border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088]" data-id="element-1293" />
                <button onClick={() => handleSuggest('audience')} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#D4A088] hover:text-[#C28B73]" data-id="element-1294">
                  <Wand2Icon size={20} data-id="element-1295" />
                </button>
              </div>
            </div>
            {/* 4. Tone and Style */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1296">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-1297">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-1298">
                  4
                </span>
                Tone and Style
              </h2>
              <div className="space-y-6" data-id="element-1299">
                {/* Tone */}
                <div className="relative" data-id="element-1300">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1301">
                    How do you want your story to sound?
                  </label>
                  <input type="text" value={storyDetails.tone} onChange={e => setStoryDetails(prev => ({
                ...prev,
                tone: e.target.value
              }))} placeholder="Describe the tone or use the magic wand for suggestions" className="w-full px-4 py-2 border-2 border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088]" data-id="element-1302" />
                  <button onClick={() => handleSuggest('tone')} className="absolute right-3 top-[38px] text-[#D4A088] hover:text-[#C28B73]" data-id="element-1303">
                    <Wand2Icon size={20} data-id="element-1304" />
                  </button>
                </div>
                {/* Writing Style */}
                <div data-id="element-1305">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1306">
                    What's Your Writing Style (optional)
                  </label>
                  <textarea value={storyDetails.writingStyle} onChange={e => setStoryDetails(prev => ({
                ...prev,
                writingStyle: e.target.value
              }))} placeholder="Copy any writing samples here to help Story Bunny get the tone right" className="w-full min-h-[120px] px-4 py-3 border-2 border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] text-[#6B4B3E]" data-id="element-1307" />
                </div>
                {/* Illustration Style */}
                <div className="relative" data-id="element-1308">
                  <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1309">
                    Illustration Style
                  </label>
                  <select value={storyDetails.illustrationStyle} onChange={e => setStoryDetails(prev => ({
                ...prev,
                illustrationStyle: e.target.value
              }))} className="w-full px-4 py-2 border-2 border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088] appearance-none" data-id="element-1310">
                    <option value="" data-id="element-1311">Select a style</option>
                    <option value="watercolor" data-id="element-1312">Watercolor Dreams</option>
                    <option value="cartoon" data-id="element-1313">Playful Cartoon</option>
                    <option value="digital" data-id="element-1314">Modern Digital</option>
                    <option value="classic" data-id="element-1315">Classic Storybook</option>
                  </select>
                  <ChevronDownIcon size={20} className="absolute right-4 top-[42px] text-[#D4A088]" data-id="element-1316" />
                </div>
              </div>
            </div>
            {/* 5. Finishing Touches */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1317">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-1318">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-1319">
                  5
                </span>
                Finishing Touches
              </h2>
              <div className="relative" data-id="element-1320">
                <textarea value={storyDetails.specialTouches} onChange={e => setStoryDetails(prev => ({
              ...prev,
              specialTouches: e.target.value
            }))} placeholder="Add any special elements, plot twists, or extra details you'd like to include in your story" className="w-full min-h-[120px] px-4 py-3 border-2 border-[#E5C1A9] rounded-xl focus:outline-none focus:border-[#D4A088] text-[#6B4B3E] placeholder-[#6B4B3E]/60" data-id="element-1321" />
              </div>
            </div>
            {/* 6. Title */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-8" data-id="element-1322">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-1323">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-1324">
                  6
                </span>
                Title
              </h2>
              <div className="relative" data-id="element-1325">
                <input type="text" value={storyDetails.title} onChange={e => setStoryDetails(prev => ({
              ...prev,
              title: e.target.value
            }))} placeholder="Enter a title or use the magic wand for suggestions based on your story" className="w-full px-4 py-2 border-2 border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088]" data-id="element-1326" />
                <button onClick={generateTitleFromContent} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#D4A088] hover:text-[#C28B73]" data-id="element-1327">
                  <Wand2Icon size={20} data-id="element-1328" />
                </button>
              </div>
            </div>
            {/* Preview Button */}
            <button onClick={() => setShowReview(true)} className="w-full bg-[#D4A088] text-white py-4 rounded-full hover:bg-[#C28B73] transition-colors flex items-center justify-center" data-id="element-1329">
              <SparklesIcon size={20} className="mr-2" data-id="element-1330" />
              Generate Preview
            </button>
          </> : <div data-id="element-1331">Loading your story...</div>}
        {/* Review Modal */}
        {showReview && <StoryReviewModal userInput={{
        foundation: {
          title: storyDetails.title,
          ageGroup: storyDetails.audience,
          storyArc: storyBasics.beginning + '\n\n' + storyBasics.ending
        },
        character: {
          name: '',
          special: '',
          type: '',
          description: storyBasics.characters
        },
        structure: {
          beginning: storyBasics.beginning,
          middle: '',
          ending: storyBasics.ending
        },
        style: {
          tone: storyDetails.tone,
          inspiration: storyDetails.writingStyle,
          special: storyDetails.specialTouches,
          illustrationStyle: storyDetails.illustrationStyle
        }
      }} onClose={() => setShowReview(false)} onEdit={() => setShowReview(false)} onConfirm={() => {
        setCurrentStep('illustrations');
        setShowReview(false);
      }} data-id="element-1332" />}
      </div>
    </div>;
};